import type { ChangeEvent, TouchEvent } from "react";
import type { CartItem, StatusMessage } from "../../types";
import { IconX } from "../common/Icons";
import QuantityInput from "../catalog/QuantityInput";

interface CartSummaryProps {
  items: CartItem[];
  totalNet: number;
  totalGross: number;
  totalDiscount?: number;
  totalDiscountPercent?: number;
  totalNetAfterDiscount?: number;
  totalGrossAfterDiscount?: number;
  notes: string;
  onNotesChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  onRemoveItem: (productId: number) => void;
  onQuantityChange: (productId: number, quantity: number) => void;
  deliveryDate: string;
  onDeliveryDateChange: (nextValue: string) => void;
  minDeliveryDate: string;
  existingOrderMessage?: string;
  hasExistingOrder?: boolean;
  status?: StatusMessage | null;
  showInlineStatus?: boolean;
  isMobileVisible?: boolean;
  isMobileFloating?: boolean;
  onCloseMobile?: () => void;
  onTouchStart?: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchMove?: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchEnd?: () => void;
}

const CartSummary = ({
  items,
  totalNet,
  totalGross,
  totalDiscount = 0,
  totalDiscountPercent = 0,
  totalNetAfterDiscount,
  totalGrossAfterDiscount,
  notes,
  onNotesChange,
  onSubmit,
  onRemoveItem,
  onQuantityChange,
  deliveryDate,
  onDeliveryDateChange,
  minDeliveryDate,
  existingOrderMessage = "",
  hasExistingOrder = false,
  status = null,
  showInlineStatus = true,
  isMobileVisible = true,
  isMobileFloating = false,
  onCloseMobile,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}: CartSummaryProps) => {
  const hasItems = items.length > 0;
  const hasError = status?.type === "error" && status?.message;
  const hasSuccess = status?.type === "success" && status?.message;
  const infoMessage =
    !hasSuccess &&
    hasExistingOrder &&
    existingOrderMessage &&
    (!hasError || status?.message !== existingOrderMessage)
      ? existingOrderMessage
      : "";
  const hasInfo = Boolean(infoMessage);
  const hasDiscount = Number.isFinite(totalDiscount) && totalDiscount > 0;
  const discountPercentLabel = Number.isFinite(totalDiscountPercent)
    ? totalDiscountPercent
    : 0;
  const handleDeliveryDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    onDeliveryDateChange(event.target.value);
  };
  const canSubmit = hasItems && Boolean(deliveryDate);
  const cartClassName = [
    "cart",
    isMobileFloating ? "cart-floating" : "cart-desktop",
    isMobileFloating && !isMobileVisible ? "hidden" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <aside className={cartClassName}>
      <div
        className="cart-header"
        onTouchStart={isMobileFloating ? onTouchStart : undefined}
        onTouchMove={isMobileFloating ? onTouchMove : undefined}
        onTouchEnd={isMobileFloating ? onTouchEnd : undefined}
      >
        <div className="cart-title">
          <h2>Koszyk</h2>
          <span className="cart-count">{items.length} poz.</span>
        </div>
        {isMobileFloating && (
          <button
            type="button"
            className="close-cart"
            onClick={onCloseMobile}
            aria-label="Zamknij koszyk"
          >
            <span className="close-cart-label">Zamknij</span>
            <span className="close-cart-icon" aria-hidden="true">
              <IconX />
            </span>
          </button>
        )}
      </div>
      <div className="cart-body">
        {showInlineStatus && hasError && (
          <div className="cart-inline-status error">{status.message}</div>
        )}
        {showInlineStatus && hasInfo && (
          <div className="cart-inline-status info">{infoMessage}</div>
        )}
        {items.length === 0 ? (
          <p className="empty">Koszyk jest pusty.</p>
        ) : (
          <ul className="cart-list">
            {items.map((item) => {
              const isUnavailable = item?.isAvailable === false;
              const name = item.displayName || item.name;
              return (
                <li
                  key={item.id}
                  className={
                    isUnavailable ? "cart-item-unavailable" : undefined
                  }
                >
                  <div className="cart-item-header">
                    <strong>{name}</strong>
                    <button
                      type="button"
                      className="cart-item-remove"
                      onClick={() => onRemoveItem(item.id)}
                      aria-label="Usuń produkt"
                      title="Usuń produkt"
                    >
                      <IconX />
                    </button>
                  </div>
                  {isUnavailable && (
                    <span className="cart-item-warning">
                      Niedostępne w wybranym dniu dostawy
                    </span>
                  )}
                  <div className="cart-item-footer">
                    <span className="cart-item-spec">
                      {item.unit} × {item.price.toFixed(2)} zł
                    </span>
                    <QuantityInput
                      value={item.quantity}
                      onChange={(newQuantity) => {
                        if (newQuantity === 0) {
                          onRemoveItem(item.id);
                        } else {
                          onQuantityChange(item.id, newQuantity);
                        }
                      }}
                      minQuantity={1}
                      step={1}
                    />
                    <span className="line-total">
                      {item.lineNet?.toFixed(2) ?? "0.00"} zł
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="cart-footer">
        {hasItems && (
          <div className="cart-summary-container">
            {hasDiscount && (
              <div className="cart-summary-row">
                <strong>Razem:</strong>
                <div className="cart-summary-values">
                  <div>
                    netto{" "}
                    <span>
                      {Number.isFinite(totalNet) ? totalNet.toFixed(2) : "0.00"}{" "}
                      zł
                    </span>
                  </div>
                  <div>
                    brutto{" "}
                    <span>
                      {Number.isFinite(totalGross)
                        ? totalGross.toFixed(2)
                        : "0.00"}{" "}
                      zł
                    </span>
                  </div>
                </div>
              </div>
            )}
            {hasDiscount && (
              <>
                <div className="cart-summary-row cart-discount-row">
                  <strong>Rabat:</strong>
                  <div className="cart-summary-values">
                    <div>
                      -
                      {Number.isFinite(totalDiscount)
                        ? totalDiscount.toFixed(2)
                        : "0.00"}{" "}
                      zł
                      {discountPercentLabel
                        ? ` (${discountPercentLabel.toFixed(2)}%)`
                        : ""}
                    </div>
                  </div>
                </div>
                <div className="cart-summary-row cart-final-row">
                  <strong>Po rabacie:</strong>
                  <div className="cart-summary-values">
                    <div>
                      netto{" "}
                      <span>
                        {Number.isFinite(totalNetAfterDiscount)
                          ? totalNetAfterDiscount.toFixed(2)
                          : "0.00"}{" "}
                        zł
                      </span>
                    </div>
                    <div>
                      brutto{" "}
                      <span>
                        {Number.isFinite(totalGrossAfterDiscount)
                          ? totalGrossAfterDiscount.toFixed(2)
                          : "0.00"}{" "}
                        zł
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
            {!hasDiscount && (
              <div className="cart-summary-row cart-final-row">
                <strong>Do zapłaty:</strong>
                <div className="cart-summary-values">
                  <div>
                    netto{" "}
                    <span>
                      {Number.isFinite(totalNet) ? totalNet.toFixed(2) : "0.00"}{" "}
                      zł
                    </span>
                  </div>
                  <div>
                    brutto{" "}
                    <span>
                      {Number.isFinite(totalGross)
                        ? totalGross.toFixed(2)
                        : "0.00"}{" "}
                      zł
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <label className="delivery-date" htmlFor="deliveryDate">
          <span>Data dostawy</span>
          <input
            id="deliveryDate"
            name="deliveryDate"
            type="date"
            value={deliveryDate || ""}
            min={minDeliveryDate || ""}
            onChange={handleDeliveryDateChange}
          />
        </label>
        <label className="notes" htmlFor="notes">
          <span>Notatki do zamówienia</span>
          <textarea
            id="notes"
            name="notes"
            rows={4}
            value={notes}
            onChange={(event) => onNotesChange(event.target.value)}
            placeholder="Dostawa na 6:30, dołącz FV itp."
            autoComplete="off"
          />
        </label>
      </div>
      <button className="primary" onClick={onSubmit} disabled={!canSubmit}>
        Złóż zamówienie
      </button>
    </aside>
  );
};

export default CartSummary;
